import React, { useEffect, useState } from 'react';
import {
    Button,
    TextField,
    Autocomplete,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    IconButton,
    Tooltip
} from '@mui/material';
import DraftEditor from './DraftEditor';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';

const Recommendations = ({
    updateRecommendations,
    recommendations
}) => {
    const [recommendationName, setRecommendationName] = useState('');
    const [description, setDescription] = useState('');
    const [recommendationsList, setRecommendationsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        if (recommendations && recommendations.length > 0) {
            setRecommendationsList(recommendations);
        }
    }, [recommendations]);

    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'asc';
        setSortBy(column);
        setSortOrder(isAsc ? 'desc' : 'asc');
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!recommendationName || !description) {
            alert('Please fill all fields');
            return;
        }

        setLoading(true);

        const newRecommendation = {
            name: recommendationName,
            description: description
        };

        const updatedRecommendations = [...recommendations, newRecommendation];
        setRecommendationsList(updatedRecommendations);
        updateRecommendations(updatedRecommendations);
        setLoading(false);
        clearFormData();
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setEditFormData({
            name: recommendations[index].name,
            description: recommendations[index].description
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = () => {
        if (!editFormData.name || !editFormData.description) {
            alert('Please fill all fields');
            return;
        }

        const updatedRecommendations = [...recommendationsList];
        updatedRecommendations[editingIndex] = {
            name: editFormData.name,
            description: editFormData.description
        };
        setRecommendationsList(updatedRecommendations);
        updateRecommendations(updatedRecommendations);
        setEditingIndex(null);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
    };

    const handleDelete = (index) => {
        if (window.confirm("Are you sure you want to delete this recommendation?")) {
            const updatedRecommendations = [...recommendationsList];
            updatedRecommendations.splice(index, 1);
            setRecommendationsList(updatedRecommendations);
            updateRecommendations(updatedRecommendations);

            if (editingIndex === index) {
                setEditingIndex(null);
            }
        }
    };

    const clearFormData = () => {
        setRecommendationName('');
        setDescription('');
    };

    const sortedRecommendations = [...recommendationsList].sort((a, b) => {
        if (!sortBy) return 0;
        return sortOrder === 'asc'
            ? a[sortBy].localeCompare(b[sortBy])
            : b[sortBy].localeCompare(a[sortBy]);
    });

    return (
        <div style={{ padding: '16px' }}>
            <Typography variant="h6" gutterBottom>Recommendation Details</Typography>

            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        label="Recommendation Name"
                        fullWidth
                        value={recommendationName}
                        onChange={(e) => setRecommendationName(e.target.value)}
                        required
                    />
                </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom>Description</Typography>
            <DraftEditor
                handleDescription={(data) => setDescription(data)}
                description={description}
                clearEditor={!description}
            />

            <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                style={{ marginTop: '16px', marginBottom: '24px', background: '#0a0068', }}
                disabled={loading}
            >
                {loading ? 'Submitting...' : 'Add Recommendation'}
            </Button>

            {recommendationsList.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                
                                <TableCell>
                                    <TableSortLabel
                                        active={sortBy === 'name'}
                                        direction={sortBy === 'name' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('name')}
                                    >
                                        Interpretation Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRecommendations.map((recommendation, index) => (
                                <TableRow key={recommendation.id}>
                                    {editingIndex === index ? (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    value={editFormData.name}
                                                    onChange={(e) => handleEditChange('name', e.target.value)}
                                                    fullWidth
                                                    required
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <DraftEditor
                                                    handleDescription={(data) => handleEditChange('description', data)}
                                                    description={editFormData.description}
                                                    clearEditor={false}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={handleSaveEdit} color="primary">
                                                    <SaveIcon />
                                                </IconButton>
                                                <IconButton onClick={handleCancelEdit} color="secondary">
                                                    <CancelIcon />
                                                </IconButton>
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell>{recommendation.name}</TableCell>
                                            <TableCell>
                                                <Tooltip
                                                    title={<div dangerouslySetInnerHTML={{ __html: recommendation.description }} />}
                                                    placement="right"
                                                    arrow
                                                >
                                                    <IconButton>
                                                        <InfoIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleEdit(index)} color="primary">
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => handleDelete(index)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    );
};

export default Recommendations;